/**
 * La «i» accanto a ogni dato della BI (Davide 27/10/2026): cosa e' il numero
 * e come e' stato calcolato, coi valori veri del periodo. Si apre al click
 * (anche sul telefono, dove non c'e' hover) e si chiude con Esc, un click
 * fuori o uno scroll.
 *
 * Il fumetto e' ``position: fixed`` calcolato dal tasto: dentro una tabella
 * che scorre in orizzontale un ``absolute`` verrebbe tagliato.
 */

import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

export type Segno = "+" | "−" | "×" | "÷" | "=";

export interface RigaConto {
  segno?: Segno;
  etichetta: string;
  valore: string;
}

export interface Spiegazione {
  titolo: string;
  testo: string;
  /** I passaggi del conto, coi numeri veri. L'ultima riga col «=» e' il risultato. */
  conto?: RigaConto[];
  /** Cosa rende il dato incerto, se lo e'. */
  incerto?: string;
}

const LARGHEZZA = 320;
const MARGINE = 12;

export function InfoTip({
  spiegazione,
  className = "",
}: {
  spiegazione: Spiegazione;
  className?: string;
}) {
  const [aperto, setAperto] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number; sopra: boolean } | null>(null);
  const tasto = useRef<HTMLButtonElement>(null);
  const fumetto = useRef<HTMLDivElement>(null);
  const id = useId();

  const chiudi = useCallback(() => setAperto(false), []);

  useLayoutEffect(() => {
    if (!aperto || !tasto.current) return;
    const r = tasto.current.getBoundingClientRect();
    const larghezza = Math.min(LARGHEZZA, window.innerWidth - MARGINE * 2);
    const left = Math.max(
      MARGINE,
      Math.min(r.left + r.width / 2 - larghezza / 2, window.innerWidth - larghezza - MARGINE),
    );
    const altezza = fumetto.current?.offsetHeight ?? 220;
    const sopra = r.bottom + altezza + MARGINE > window.innerHeight && r.top > altezza + MARGINE;
    setPos({ top: sopra ? r.top - altezza - 8 : r.bottom + 8, left, sopra });
  }, [aperto]);

  useEffect(() => {
    if (!aperto) return;
    const fuori = (e: PointerEvent) => {
      const t = e.target as Node;
      if (!tasto.current?.contains(t) && !fumetto.current?.contains(t)) chiudi();
    };
    const tasti = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        chiudi();
        tasto.current?.focus();
      }
    };
    document.addEventListener("pointerdown", fuori);
    document.addEventListener("keydown", tasti);
    window.addEventListener("scroll", chiudi, true);
    window.addEventListener("resize", chiudi);
    return () => {
      document.removeEventListener("pointerdown", fuori);
      document.removeEventListener("keydown", tasti);
      window.removeEventListener("scroll", chiudi, true);
      window.removeEventListener("resize", chiudi);
    };
  }, [aperto, chiudi]);

  return (
    <>
      <button
        ref={tasto}
        type="button"
        aria-label={`Come si calcola: ${spiegazione.titolo}`}
        aria-expanded={aperto}
        aria-controls={aperto ? id : undefined}
        onClick={(e) => {
          e.stopPropagation();
          setAperto((v) => !v);
        }}
        className={`inline-grid h-[18px] w-[18px] shrink-0 place-items-center rounded-full border text-[11px] font-bold italic leading-none transition-[background-color,color,border-color,transform] duration-150 [font-family:Georgia,serif] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/50 active:scale-90 max-md:h-6 max-md:w-6 max-md:text-[13px] ${
          aperto
            ? "border-ink bg-ink text-on-ink"
            : "border-line-2 bg-card text-ink hover:border-ink hover:bg-paper-2"
        } ${className}`}
      >
        i
      </button>
      {aperto &&
        createPortal(
          <div
            ref={fumetto}
            id={id}
            role="dialog"
            aria-label={spiegazione.titolo}
            style={{
              top: pos?.top ?? -9999,
              left: pos?.left ?? -9999,
              width: Math.min(LARGHEZZA, window.innerWidth - MARGINE * 2),
            }}
            className={`bi-fumetto fixed z-[80] rounded-[14px] border border-line-2 bg-card p-4 text-left text-[13px] leading-[1.45] text-ink shadow-[0_14px_40px_-12px_rgba(31,27,22,0.35),0_2px_6px_rgba(31,27,22,0.08)] ${
              pos?.sopra ? "origin-bottom" : "origin-top"
            }`}
          >
            <p className="font-display text-[15px] font-bold leading-tight">{spiegazione.titolo}</p>
            <p className="mt-1.5">{spiegazione.testo}</p>
            {spiegazione.conto && spiegazione.conto.length > 0 && (
              <div className="mt-3 rounded-[10px] bg-paper-2 px-3 py-2.5">
                <p className="mb-1.5 text-[11.5px] font-bold">Come e' calcolato</p>
                <dl className="space-y-0.5 tabular-nums">
                  {spiegazione.conto.map((riga, i) => (
                    <div
                      key={`${riga.etichetta}-${i}`}
                      className={`flex items-baseline gap-2 ${
                        riga.segno === "=" ? "mt-1 border-t border-line-2 pt-1 font-bold" : ""
                      }`}
                    >
                      <span className="w-3 shrink-0 text-center font-bold" aria-hidden="true">
                        {riga.segno ?? ""}
                      </span>
                      <dt className="min-w-0 flex-1">{riga.etichetta}</dt>
                      <dd className="shrink-0 font-semibold">{riga.valore}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}
            {spiegazione.incerto && (
              <p className="mt-3 flex gap-2 rounded-[10px] bg-amber-soft px-3 py-2 text-[12.5px]">
                <span aria-hidden="true" className="font-bold">
                  ≈
                </span>
                <span>{spiegazione.incerto}</span>
              </p>
            )}
            <p className="mt-3 text-[11.5px] font-semibold">Importi senza IVA.</p>
          </div>,
          document.body,
        )}
    </>
  );
}

/**
 * Quanto e' affidabile un numero: ordini STIMATI (il margine c'e', con un costo
 * non certo) e ordini SENZA MARGINE (un costo manca, il margine non si
 * calcola). Niente etichetta = tutto certo.
 */
export function Incerto({
  stimati,
  mancanti,
  compatto = false,
}: {
  stimati: number;
  mancanti: number;
  compatto?: boolean;
}) {
  if (stimati <= 0 && mancanti <= 0) return null;
  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      {stimati > 0 && (
        <span
          title={`${stimati} ordini con margine stimato`}
          className="inline-flex h-[20px] items-center gap-1 whitespace-nowrap rounded-full bg-amber-soft px-2 text-[11.5px] font-bold text-ink"
        >
          <span aria-hidden="true">≈</span>
          {compatto ? stimati : `${stimati} stimati`}
        </span>
      )}
      {mancanti > 0 && (
        <span
          title={`${mancanti} ordini senza margine: manca un costo`}
          className="inline-flex h-[20px] items-center gap-1 whitespace-nowrap rounded-full bg-red-soft px-2 text-[11.5px] font-bold text-ink"
        >
          <span aria-hidden="true">?</span>
          {compatto ? mancanti : `${mancanti} senza margine`}
        </span>
      )}
    </span>
  );
}
